"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import NoteEditor from "@/components/NoteEditor";
import HistoryPanel from "@/components/HistoryPanel";
import DaySummaryPanel from "@/components/DaySummaryPanel";
import WeeklySummaryPanel from "@/components/WeeklySummaryPanel";
import { Note } from "@/types/note";
import { useSwipe } from "@/hooks/useSwipe";
import { useOCAccount } from "@/hooks/useOCAccount";
import { OCAnalytics } from "@opencampus/ochub-utils";
import { Logo, LogoIcon } from "@opencampus/ochub-ui";

// Fun learner jargon for non-authenticated users
const LEARNER_JARGON = [
  "Knowledge Seeker",
  "Curious Mind",
  "Brainy Explorer",
  "Study Ninja",
  "Wisdom Hunter",
  "Learning Legend",
  "Note Ninja",
  "Campus Explorer",
];

function getRandomLearnerJargon(): string {
  return LEARNER_JARGON[Math.floor(Math.random() * LEARNER_JARGON.length)];
}

type PanelType = "day-summary" | "main" | "weekly-summary";

export default function HomeContent() {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [currentPanel, setCurrentPanel] = useState<PanelType>("main");
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [notesVersion, setNotesVersion] = useState(0);
  const [learnerJargon] = useState(() => getRandomLearnerJargon());

  // Get OCAccount authentication status
  const { isAuthenticated, ocId } = useOCAccount();

  // Initialize analytics on mount
  const analyticsInitialized = useRef(false);
  useEffect(() => {
    if (!analyticsInitialized.current) {
      OCAnalytics.initialize("OCNote", { containerId: "G-VNN1SC4GEG" });
      analyticsInitialized.current = true;
    }
  }, []);

  const handleNewNote = () => {
    setSelectedNote(null);
    setIsEditorOpen(true);

    // Track add_new_note event
    try {
      OCAnalytics.getInstance().trackEvent("add_new_note", {
        ocid: ocId,
      });
    } catch (e) {
      // Analytics not initialized yet, ignore
    }
  };

  const handleSelectNote = (note: Note) => {
    setSelectedNote(note);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setSelectedNote(null);
  };

  const handleSave = () => {
    setIsEditorOpen(false);
    setSelectedNote(null);
    // Trigger refresh of summary panel
    setNotesVersion((v) => v + 1);
  };

  const showDaySummaryPanel = useCallback(() => {
    setCurrentPanel("day-summary");
    setSwipeOffset(0);
  }, []);

  const showMainPanel = useCallback(() => {
    setCurrentPanel("main");
    setSwipeOffset(0);
  }, []);

  const showWeeklySummaryPanel = useCallback(() => {
    setCurrentPanel("weekly-summary");
    setSwipeOffset(0);
  }, []);

  const handleSwipeProgress = useCallback((progress: number) => {
    // Convert progress (-1 to 1) to pixel offset for visual feedback
    setSwipeOffset(progress * 100);
  }, []);

  // Handle swipe based on current panel
  const handleSwipeRight = useCallback(() => {
    if (currentPanel === "main") {
      showDaySummaryPanel();
    } else if (currentPanel === "weekly-summary") {
      showMainPanel();
    }
  }, [currentPanel, showDaySummaryPanel, showMainPanel]);

  const handleSwipeLeft = useCallback(() => {
    if (currentPanel === "main") {
      showWeeklySummaryPanel();
    } else if (currentPanel === "day-summary") {
      showMainPanel();
    }
  }, [currentPanel, showMainPanel, showWeeklySummaryPanel]);

  // Swipe handlers with progress tracking
  const swipeHandlers = useMemo(
    () => ({
      onSwipeRight: handleSwipeRight,
      onSwipeLeft: handleSwipeLeft,
      onSwipeProgress: handleSwipeProgress,
    }),
    [handleSwipeRight, handleSwipeLeft, handleSwipeProgress],
  );

  const { isSwiping } = useSwipe(swipeHandlers);

  // Calculate carousel transform based on current panel and swipe offset
  // 3 panels: day-summary (0), main (-100vw), weekly-summary (-200vw)
  const carouselStyle = useMemo(() => {
    let baseOffset = -100; // Default to main panel (center)
    if (currentPanel === "day-summary") {
      baseOffset = 0;
    } else if (currentPanel === "weekly-summary") {
      baseOffset = -200;
    }

    return {
      transform: `translateX(calc(${baseOffset}vw + ${isSwiping ? swipeOffset : 0}px))`,
      transition: isSwiping
        ? "none"
        : "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    };
  }, [currentPanel, swipeOffset, isSwiping]);

  return (
    <div className="min-h-screen overflow-hidden relative">
      {/* History button - outside carousel so it's always visible on main panel */}
      {currentPanel === "main" && (
        <button
          onClick={() => setIsHistoryOpen(true)}
          className="fixed top-6 right-6 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:shadow-xl transform hover:scale-110 transition-all z-30 border border-gray-100"
          title="View note history"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-brand-blue"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      )}

      {/* Left arrow button for day summary panel - outside carousel so it's always visible */}
      {currentPanel === "main" && (
        <button
          onClick={showDaySummaryPanel}
          className="fixed left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 shadow-lg border border-gray-100 hover:bg-white hover:shadow-xl transition-all arrow-breathe"
          title="View day summary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-brand-blue"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}

      {/* Right arrow button for weekly summary panel - outside carousel so it's always visible */}
      {currentPanel === "main" && (
        <button
          onClick={showWeeklySummaryPanel}
          className="fixed right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 shadow-lg border border-gray-100 hover:bg-white hover:shadow-xl transition-all arrow-breathe"
          title="View weekly summary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-brand-blue"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      )}

      {/* Carousel container - 3 panels */}
      <div className="carousel-container-3" style={carouselStyle}>
        {/* Day Summary Panel (Left) */}
        <div className="carousel-panel summary-panel">
          <DaySummaryPanel onClose={showMainPanel} refreshKey={notesVersion} />
        </div>

        {/* Main Panel (Center) */}
        <div className="carousel-panel main-panel">
          <main className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-brand-cyan/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-brand-blue/10 rounded-full blur-3xl" />
            </div>

            {/* Logo and title */}
            <div className="text-center mb-12 z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-brand-blue to-brand-cyan rounded-2xl shadow-lg mb-4">
                {/* Notepad Icon */}
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Shadow layer for depth */}
                  <rect
                    x="6.5"
                    y="4.5"
                    width="12"
                    height="16"
                    rx="1.5"
                    fill="rgba(0,0,0,0.15)"
                  />
                  {/* Main notepad body */}
                  <rect
                    x="5"
                    y="3"
                    width="12"
                    height="16"
                    rx="1.5"
                    fill="white"
                  />
                  {/* Subtle gradient overlay for paper texture */}
                  <rect
                    x="5"
                    y="3"
                    width="12"
                    height="16"
                    rx="1.5"
                    fill="url(#paperGradient)"
                  />
                  {/* Top binding bar */}
                  <rect
                    x="5"
                    y="3"
                    width="12"
                    height="2.5"
                    rx="1.5"
                    fill="#141beb"
                    opacity="0.9"
                  />
                  {/* Content lines - simple and clean */}
                  <line
                    x1="7"
                    y1="8"
                    x2="15"
                    y2="8"
                    stroke="#141beb"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="7"
                    y1="11"
                    x2="14"
                    y2="11"
                    stroke="#141beb"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    opacity="0.7"
                  />
                  <line
                    x1="7"
                    y1="14"
                    x2="12"
                    y2="14"
                    stroke="#141beb"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    opacity="0.4"
                  />
                  {/* Gradient definition */}
                  <defs>
                    <linearGradient
                      id="paperGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="white" stopOpacity="0" />
                      <stop
                        offset="100%"
                        stopColor="#f0f0f0"
                        stopOpacity="0.5"
                      />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              {/* Title with OpenCampus logo icon */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <LogoIcon className="w-8 h-8" />
                <h1 className="text-3xl font-bold gradient-text">OCNote</h1>
              </div>

              {/* Login status box */}
              <div className="mb-3 px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-cyan rounded-lg shadow-md">
                <p className="text-white text-sm font-medium">
                  {isAuthenticated
                    ? `Welcome, ${ocId}`
                    : `Welcome, ${learnerJargon}`}
                </p>
              </div>

              <p className="text-gray-600">Quick notes for your classes</p>
            </div>

            {/* Main action button - big circular button */}
            <button
              onClick={handleNewNote}
              className="relative z-10 w-32 h-32 bg-gradient-to-br from-brand-blue to-brand-cyan rounded-full shadow-2xl flex items-center justify-center transform hover:scale-110 transition-all duration-300 animate-pulse-soft group"
            >
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-blue to-brand-cyan opacity-0 group-hover:opacity-100 blur-xl transition-opacity" />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-14 w-14 text-white relative z-10"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>

            <p className="mt-6 text-gray-500 text-sm z-10">
              Tap to start a new note
            </p>

            {/* Powered by footer */}
            <div className="fixed bottom-6 left-0 right-0 flex items-center justify-center gap-2 z-10">
              <span className="text-gray-400 text-xs">Powered by</span>
              <Logo variant="dark" className="h-4 w-auto" />
            </div>
          </main>
        </div>

        {/* Weekly Summary Panel (Right) */}
        <div className="carousel-panel weekly-panel">
          <WeeklySummaryPanel
            onClose={showMainPanel}
            refreshKey={notesVersion}
          />
        </div>
      </div>

      {/* Note Editor */}
      {isEditorOpen && (
        <NoteEditor
          existingNote={selectedNote}
          onClose={handleCloseEditor}
          onSave={handleSave}
        />
      )}

      {/* History Panel */}
      <HistoryPanel
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onSelectNote={handleSelectNote}
        onNotesChange={() => {}}
      />
    </div>
  );
}
